import { useState, useRef, useEffect } from 'react';
import { Shield, X, Mail } from 'lucide-react';

interface Props {
  title?: string;
  description?: string;
  onSubmit: (code: string) => Promise<void>;
  onCancel?: () => void;
  /** Hvis true, vises som modal-overlay. Hvis false, vises inline. */
  modal?: boolean;
  /** Tilgængelige 2FA-metoder. Hvis email er med, vises "Send kode til email" knap. */
  methods?: Array<'totp' | 'email'>;
  /** Callback når brugeren beder om at få sendt en email-kode */
  onSendEmailCode?: () => Promise<void>;
}

// Genbrugelig 2FA-prompt — bruges på login-trin 2 og ved følsomme aktioner
// (backup-download). Accepterer TOTP-, email- eller recovery-kode.
// Hvis 'email' er i methods, vises en "Send kode til email"-knap.
export default function TotpPrompt({
  title = 'To-faktor bekræftelse',
  description,
  onSubmit,
  onCancel,
  modal = true,
  methods,
  onSendEmailCode,
}: Props) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const hasTotp = !methods || methods.includes('totp');
  const hasEmail = methods?.includes('email') && !!onSendEmailCode;
  const computedDescription = description ?? (
    hasTotp && hasEmail
      ? 'Indtast kode fra din authenticator-app, en recovery-kode, eller bed om en email-kode nedenfor.'
      : hasTotp
        ? 'Indtast den 6-cifrede kode fra din authenticator-app, eller en recovery-kode (XXXX-XXXX).'
        : 'Klik "Send kode til email" nedenfor for at modtage en 6-cifret kode på din email.'
  );

  const handleSendEmail = async () => {
    if (!onSendEmailCode || sendingEmail) return;
    setSendingEmail(true);
    setError(''); setInfo('');
    try {
      await onSendEmailCode();
      setInfo('En 6-cifret kode er sendt til din email. Den udløber om 5 minutter.');
      inputRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke sende kode');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Forkert kode');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const inner = (
    <form onSubmit={handleSubmit} className="bg-cream-50 rounded-card shadow-card-md p-6 max-w-sm w-full">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-sage-100 text-sage-700 flex items-center justify-center">
            <Shield size={18} />
          </span>
          <h3 className="modal-title">{title}</h3>
        </div>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-ink-300 hover:text-ink-700 -mr-1.5">
            <X size={18} />
          </button>
        )}
      </div>

      <p className="text-sm text-ink-500 mb-4">{computedDescription}</p>

      <input
        ref={inputRef}
        type="text"
        value={code}
        onChange={e => setCode(e.target.value)}
        placeholder="123 456"
        autoComplete="one-time-code"
        inputMode="text"
        className="input text-center tracking-widest text-lg font-mono"
        disabled={submitting}
      />

      {info && <p className="text-sm text-sage-700 mt-2">{info}</p>}
      {error && <p className="text-sm text-rose-700 mt-2">{error}</p>}

      <div className="flex gap-2 mt-4">
        <button type="submit" disabled={submitting || !code.trim()} className="btn btn-primary flex-1">
          {submitting ? 'Verificerer…' : 'Bekræft'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Annuller
          </button>
        )}
      </div>

      {hasEmail && (
        <div className="mt-4 pt-4 border-t border-cream-100">
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="text-sm text-sage-700 hover:text-sage-900 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Mail size={14} />
            {sendingEmail ? 'Sender…' : 'Send kode til min email i stedet'}
          </button>
        </div>
      )}
    </form>
  );

  if (!modal) return inner;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      {inner}
    </div>
  );
}
